import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { ROUTES, buildRoute } from '../../constants/routes';
import { useListOffersQuery } from '../../store/siteContentApi';

// Brand evergreen fallbacks — render only when the API has zero in-window
// offers. Once the admin publishes an offer, these step aside entirely.
const FALLBACK_BANNERS = [
  {
    id: 'fallback-1',
    eyebrow: 'Limited time',
    title: 'Full Body Checkup',
    subtitle: '91 parameters · doctor-verified · home collection in 60 minutes',
    finalPrice: 1599,
    originalPrice: 2400,
    savePct: 33,
    cta: 'Book now →',
    href: ROUTES.PACKAGES,
    parameters: [],
    sampleType: 'Blood',
    reportTat: 'Same day',
    nabl: true,
  },
  {
    id: 'fallback-2',
    eyebrow: 'New launch',
    title: 'Diabetes Care Package',
    subtitle: 'HbA1c, fasting glucose, lipid profile, kidney function — bundled.',
    finalPrice: 999,
    originalPrice: 1500,
    savePct: 33,
    cta: 'Book now →',
    href: ROUTES.PACKAGES,
    parameters: [],
    sampleType: 'Blood',
    reportTat: '4 hours',
    nabl: true,
  },
  {
    id: 'fallback-3',
    eyebrow: 'TrustLab+ Members',
    title: 'Save 25% on every test',
    subtitle: 'Annual membership at ₹999 — pays for itself in two tests.',
    finalPrice: 999,
    originalPrice: 0,
    savePct: 0,
    cta: 'Join now →',
    href: ROUTES.MEMBERSHIP,
    parameters: [],
    sampleType: '',
    reportTat: '',
    nabl: true,
  },
];

const ROTATE_MS = 5000;
const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// Convert one Offer document into the shape this component renders.
const offerToBanner = (o) => {
  const isPercent = o.offer_type === 'percent';
  const finalPrice = isPercent
    ? Math.max(0, Math.round(Number(o.original_price || 0) * (1 - Number(o.offer_value || 0) / 100)))
    : Math.max(0, Number(o.original_price || 0) - Number(o.offer_value || 0));
  const originalPrice = Number(o.original_price) || 0;
  const savePct = originalPrice > 0 && finalPrice < originalPrice
    ? Math.round(((originalPrice - finalPrice) / originalPrice) * 100)
    : 0;
  // Comma-separated EDOS parameters → trimmed array, max 6 to fit the
  // 2-col grid without overflowing the offer panel.
  const params = String(o.parameters || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 6);
  return {
    id: o._id || o.test_id,
    eyebrow: o.campaign || o.badge || 'Special offer',
    tagline: o.tagline || '',
    title: o.title || o.test_name,
    // The actual EDOS test the offer is anchored to. The right offer
    // panel shows this so the visitor knows which test they're booking,
    // while the left poster shows the campaign-style title.
    testName: o.test_name || o.title || '',
    subtitle: o.description || (
      isPercent ? `${o.offer_value}% off — limited time` : `Save ₹${o.offer_value} on this test`
    ),
    badge: o.badge || '',
    finalPrice,
    originalPrice,
    savePct,
    parameters: params,
    sampleType: o.sample_type || '',
    reportTat: o.report_tat || '',
    nabl: !!o.nabl_certified,
    validUntil: o.valid_until || '',
    cta: 'Book now →',
    href: o.test_id ? buildRoute(ROUTES.TEST_DETAIL, { id: o.test_id }) : ROUTES.SPECIALS,
    bannerUrl: o.banner_url || '',
  };
};

const buildSlides = (offers) => {
  if (Array.isArray(offers) && offers.length > 0) return offers.slice(0, 5).map(offerToBanner);
  return FALLBACK_BANNERS;
};

// Format the validity end as "Ends 15 May" — null returns "Limited period".
const formatValidity = (iso) => {
  if (!iso) return 'Limited period';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Limited period';
  return `Valid till ${d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}`;
};

// Decorative leaf-and-bud motif. Reused top-left and bottom-right of the
// art panel via rotation. Kept as a component so the JSX in the main
// render stays scannable.
const BotanicalSvg = ({ sx }) => (
  <Box
    component="svg"
    aria-hidden
    viewBox="0 0 200 200"
    fill="none"
    sx={{ position: 'absolute', pointerEvents: 'none', ...sx }}
  >
    <path d="M40 180 Q50 120, 90 80 Q130 40, 170 30" stroke="#1A3A2A" strokeWidth="1.2" strokeLinecap="round" />
    <ellipse cx="60" cy="150" rx="14" ry="6" fill="#00B49A" opacity=".5" transform="rotate(-30 60 150)" />
    <ellipse cx="78" cy="120" rx="16" ry="7" fill="#1A3A2A" opacity=".35" transform="rotate(-40 78 120)" />
    <ellipse cx="100" cy="90" rx="18" ry="7" fill="#00B49A" opacity=".55" transform="rotate(-50 100 90)" />
    <ellipse cx="125" cy="65" rx="14" ry="6" fill="#1A3A2A" opacity=".4" transform="rotate(-60 125 65)" />
    <circle cx="155" cy="40" r="6" fill="#F0B429" opacity=".7" />
    <circle cx="155" cy="40" r="3" fill="#1A3A2A" />
  </Box>
);

// Small stat chip used in the cream art panel's "Stats row" — a tiny
// uppercase Space-Mono label sitting above a Fraunces-serif value, with
// a gold bullet to the left. Lifted from the previous deleted variants
// and tuned to the editorial palette so the row reads as one cohesive
// motif (badge row → headline → stats row, all sharing the same gold +
// forest accents).
const StatChip = ({ label, value }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.875,
      position: 'relative',
      zIndex: 1,
    }}
  >
    <Box
      aria-hidden
      sx={{
        width: 4,
        height: 4,
        borderRadius: '50%',
        bgcolor: '#F0B429',
        flexShrink: 0,
      }}
    />
    <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
      <Box
        component="span"
        sx={{
          fontFamily: "'Space Mono', monospace",
          fontSize: 9,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'rgba(26,58,42,0.65)',
          mb: 0.375,
        }}
      >
        {label}
      </Box>
      <Box
        component="span"
        sx={{
          fontFamily: "'Fraunces', serif",
          fontSize: { xs: 14, md: 16 },
          fontWeight: 600,
          color: '#1A3A2A',
          letterSpacing: '-0.005em',
        }}
      >
        {value}
      </Box>
    </Box>
  </Box>
);

// ─── Editorial tri-column banner — full-bleed 3-section design ──────────
// Left: cream "editorial" art panel (eyebrow / serif title / stats chips).
// Middle: visual spotlight — big animated golden savings disc, validity
//   stamp underneath, transitions the cream → green gradient between the
//   two outer panels.
// Right: dark forest green offer panel (test name / parameters / price /
//   CTA / fineprint).
// Used for slide #2 in the home banner carousel.
const EditorialTriBanner = ({ banner, navigate }) => {
  const tests = banner.parameters || [];
  const validityLabel = formatValidity(banner.validUntil);
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: { xs: 'auto', md: '100%' },
        minHeight: { xs: 680, md: 500 },
        overflow: 'hidden',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1.05fr 0.7fr 1.15fr' },
      }}
    >
      {/* ─── LEFT: cream art panel ─────────────────────── */}
      <Box
        sx={{
          position: 'relative',
          background: 'linear-gradient(135deg, #FBF6EC 0%, #F4ECDB 60%, #EFE3CB 100%)',
          padding: { xs: '40px 28px 28px', md: '52px 56px' },
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 2.5, md: 3 },
          justifyContent: 'center',
        }}
      >
        <BotanicalSvg sx={{ top: -30, left: -20, width: { xs: 140, md: 200 }, opacity: 0.55, transform: 'rotate(-12deg)' }} />
        <BotanicalSvg sx={{ bottom: -40, right: -30, width: { xs: 160, md: 220 }, opacity: 0.5, transform: 'rotate(160deg)' }} />

        {/* Badge row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.75,
            fontFamily: "'Space Mono', monospace",
            fontSize: 11,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: '#1A3A2A',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#F0B429', boxShadow: '0 0 0 4px rgba(240,180,41,0.18)' }} />
          <Box component="span" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {banner.eyebrow}
          </Box>
          <Box sx={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, rgba(26,58,42,0.18), transparent)', minWidth: 20 }} />
        </Box>

        {/* Headline area */}
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          {banner.tagline && (
            <Typography
              sx={{
                fontFamily: "'Pinyon Script', cursive",
                fontSize: { xs: 30, md: 40 },
                color: '#C98978',
                lineHeight: 1,
                transform: 'rotate(-2deg)',
                ml: '-4px',
                mb: 1,
              }}
            >
              {banner.tagline}
            </Typography>
          )}
          <Typography
            sx={{
              fontFamily: "'Fraunces', serif",
              fontWeight: 400,
              fontSize: { xs: 32, sm: 38, md: 44, lg: 52 },
              lineHeight: 1.05,
              color: '#1A3A2A',
              letterSpacing: '-0.015em',
            }}
          >
            {banner.title}
          </Typography>
        </Box>

        {/* Stats row */}
        <Box
          sx={{
            mt: { xs: 1.5, md: 2 },
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 2, md: 2.75 },
            flexWrap: 'wrap',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {tests.length > 0 && <StatChip label="Tests" value={String(tests.length)} />}
          {banner.reportTat && <StatChip label="Report" value={banner.reportTat} />}
          {banner.sampleType && <StatChip label="Sample" value={banner.sampleType.split(' ').slice(0, 2).join(' ')} />}
          <StatChip label="Cities" value="14 labs" />
        </Box>
      </Box>

      {/* ─── MIDDLE: visual spotlight ─────────────────
          Deep velvety forest stage with a strong gold halo, scattered
          sparkle motifs, and a fine herringbone texture. The disc reads
          as a luminous centrepiece, the validity stamp sits underneath
          on a clean cream pill. */}
      <Box
        sx={{
          position: 'relative',
          background:
            'radial-gradient(ellipse 80% 70% at 50% 45%, #1F4533 0%, #0F2419 60%, #07150D 100%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: { xs: '32px 16px', md: '40px 20px' },
          gap: { xs: 2, md: 2.5 },
          overflow: 'hidden',
          minHeight: { xs: 260, md: 'auto' },
          '@keyframes badge-spin': { to: { transform: 'rotate(360deg)' } },
          '@keyframes sparkle-twinkle': {
            '0%, 100%': { opacity: 0.4, transform: 'scale(0.9)' },
            '50%':      { opacity: 1,   transform: 'scale(1.1)' },
          },
        }}
      >
        {/* Strong gold halo behind the disc */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 50% 45%, rgba(240,180,41,0.42), rgba(240,180,41,0.12) 30%, transparent 60%)',
            pointerEvents: 'none',
          }}
        />
        {/* Cream → forest soft edge bleeds so the section seams stay
            connected to the outer panels without harsh hard cuts. */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(90deg, rgba(239,227,203,0.18) 0%, transparent 12%, transparent 88%, rgba(26,58,42,0.4) 100%)',
            pointerEvents: 'none',
          }}
        />
        {/* Diagonal hairline texture matching the right offer panel */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'repeating-linear-gradient(45deg, rgba(255,255,255,0.03) 0 1px, transparent 1px 14px)',
            pointerEvents: 'none',
          }}
        />
        {/* Twinkling gold sparkles scattered around the disc */}
        {[
          { top: '15%',  left: '18%', size: 14, delay: '0s'   },
          { top: '22%',  left: '78%', size: 10, delay: '0.6s' },
          { top: '68%',  left: '14%', size: 11, delay: '1.2s' },
          { top: '78%',  left: '82%', size: 16, delay: '0.3s' },
          { top: '50%',  left: '8%',  size: 8,  delay: '0.9s' },
          { top: '40%',  left: '88%', size: 9,  delay: '1.5s' },
        ].map((s, i) => (
          <Box
            key={i}
            aria-hidden
            sx={{
              position: 'absolute',
              top: s.top,
              left: s.left,
              fontSize: s.size,
              color: '#F0B429',
              opacity: 0.7,
              textShadow: '0 0 12px rgba(240,180,41,0.7)',
              animation: `sparkle-twinkle 2.6s ease-in-out ${s.delay} infinite`,
              pointerEvents: 'none',
              zIndex: 0,
            }}
          >
            ✦
          </Box>
        ))}

        {/* Big savings disc */}
        {banner.savePct > 0 ? (
          <Box
            sx={{
              position: 'relative',
              width: { xs: 170, md: 220 },
              height: { xs: 170, md: 220 },
              borderRadius: '50%',
              background:
                'radial-gradient(circle at 32% 32%, #FFD86A 0%, #F0B429 55%, #C8941E 100%)',
              display: 'grid',
              placeItems: 'center',
              color: '#1A3A2A',
              boxShadow:
                '0 24px 48px -12px rgba(0,0,0,0.45), inset 0 -10px 22px rgba(192,123,16,0.35), inset 0 10px 22px rgba(255,255,255,0.5)',
              zIndex: 1,
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: -12,
                borderRadius: '50%',
                border: '1.5px dashed rgba(240,180,41,0.55)',
                animation: 'badge-spin 30s linear infinite',
              },
            }}
          >
            <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
              <Box sx={{
                fontFamily: "'Space Mono', monospace",
                fontSize: { xs: 10, md: 11 },
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                fontWeight: 700,
                lineHeight: 1,
              }}>
                SAVE
              </Box>
              <Box sx={{
                fontFamily: "'Fraunces', serif",
                fontStyle: 'italic',
                fontWeight: 700,
                fontSize: { xs: 56, md: 74 },
                lineHeight: 1,
                letterSpacing: '-0.03em',
                mt: 0.75,
              }}>
                {banner.savePct}
                <Box component="span" sx={{ fontSize: { xs: 30, md: 42 } }}>%</Box>
              </Box>
              <Box sx={{
                fontFamily: "'Space Mono', monospace",
                fontSize: { xs: 10, md: 11 },
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                fontWeight: 700,
                mt: 0.75,
                lineHeight: 1,
              }}>
                OFF
              </Box>
            </Box>
          </Box>
        ) : (
          // Fallback when there's no percent — show a thematic disc with
          // an emoji so the middle column still has a focal point.
          <Box
            sx={{
              width: { xs: 170, md: 220 },
              height: { xs: 170, md: 220 },
              borderRadius: '50%',
              background:
                'radial-gradient(circle at 32% 32%, #FFD86A 0%, #F0B429 55%, #C8941E 100%)',
              display: 'grid',
              placeItems: 'center',
              fontSize: { xs: 76, md: 110 },
              boxShadow:
                '0 24px 48px -12px rgba(0,0,0,0.45), inset 0 -10px 22px rgba(192,123,16,0.35), inset 0 10px 22px rgba(255,255,255,0.5)',
              zIndex: 1,
            }}
          >
            🎁
          </Box>
        )}

        {/* Validity stamp — gold ink on dark forest pill, matching the
            new deep velvety middle panel. */}
        <Box
          sx={{
            position: 'relative', zIndex: 1,
            fontFamily: "'Space Mono', monospace",
            fontSize: 11,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: '#F0B429',
            textAlign: 'center',
            border: '1px solid rgba(240,180,41,0.4)',
            borderRadius: '100px',
            px: 1.5, py: 0.5,
            bgcolor: 'rgba(240,180,41,0.08)',
            backdropFilter: 'blur(2px)',
          }}
        >
          {validityLabel}
        </Box>
      </Box>

      {/* ─── RIGHT: dark offer panel ───────────────── */}
      <Box
        sx={{
          position: 'relative',
          background: 'linear-gradient(160deg, #1A3A2A 0%, #214938 55%, #2E5240 100%)',
          color: '#FBF6EC',
          padding: { xs: '32px 24px', md: '52px 56px' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            width: 520, height: 520,
            right: -200, top: -200,
            background: 'radial-gradient(circle, rgba(0,180,154,0.35), transparent 60%)',
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'repeating-linear-gradient(45deg, rgba(255,255,255,0.025) 0 1px, transparent 1px 14px)',
            pointerEvents: 'none',
          },
        }}
      >
        {/* Campaign tag */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            display: 'inline-flex',
            alignSelf: 'flex-start',
            alignItems: 'center',
            gap: 1,
            padding: '6px 12px',
            border: '1px solid rgba(240,180,41,0.45)',
            borderRadius: 100,
            fontFamily: "'Space Mono', monospace",
            fontSize: 10,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: '#F0B429',
            bgcolor: 'rgba(240,180,41,0.06)',
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: 6, height: 6, borderRadius: '50%', bgcolor: '#F0B429',
              '@keyframes promo-tag-pulse': {
                '0%, 100%': { boxShadow: '0 0 0 0 rgba(240,180,41,0.6)' },
                '50%':      { boxShadow: '0 0 0 6px rgba(240,180,41,0)' },
              },
              animation: 'promo-tag-pulse 1.8s ease-in-out infinite',
              flexShrink: 0,
            }}
          />
          <Box component="span" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {banner.eyebrow}
          </Box>
        </Box>

        {/* Test name */}
        <Typography
          sx={{
            position: 'relative', zIndex: 2,
            fontFamily: "'Fraunces', serif",
            fontWeight: 400,
            fontSize: { xs: 22, md: 26 },
            lineHeight: 1.15,
            letterSpacing: '-0.01em',
          }}
        >
          {banner.testName}
        </Typography>
        {banner.subtitle && (
          <Typography
            sx={{
              position: 'relative', zIndex: 2,
              mt: 0.75,
              fontSize: 13,
              color: 'rgba(251,246,236,0.65)',
              lineHeight: 1.5,
            }}
          >
            {banner.subtitle}
          </Typography>
        )}

        <Box
          sx={{
            position: 'relative', zIndex: 2,
            height: '1px',
            background: 'linear-gradient(90deg, rgba(240,180,41,0.5), rgba(240,180,41,0))',
            my: { xs: 2, md: 2.5 },
          }}
        />

        {/* Parameters — capped at 4 so the right column doesn't overflow */}
        {tests.length > 0 && (
          <Box
            sx={{
              position: 'relative', zIndex: 2,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              columnGap: 2,
              rowGap: 1,
              mb: { xs: 2.5, md: 3 },
            }}
          >
            {tests.slice(0, 4).map((p, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  fontSize: 13,
                  color: 'rgba(251,246,236,0.92)',
                  lineHeight: 1.4,
                }}
              >
                <Box
                  component="svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#00B49A"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  sx={{ flex: '0 0 14px', mt: '3px' }}
                >
                  <path d="M20 6 9 17l-5-5" />
                </Box>
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{p}</Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Price */}
        <Box
          sx={{
            position: 'relative', zIndex: 2,
            display: 'flex',
            alignItems: 'flex-end',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Fraunces', serif",
              fontWeight: 300,
              fontSize: { xs: 36, md: 46 },
              lineHeight: 1,
              letterSpacing: '-0.02em',
              color: '#FBF6EC',
            }}
          >
            <Box component="span" sx={{ fontSize: { xs: 20, md: 24 }, verticalAlign: 'top', mr: 0.25, color: '#F0B429' }}>₹</Box>
            {Number(banner.finalPrice || 0).toLocaleString('en-IN')}
          </Typography>
          {banner.originalPrice > 0 && banner.finalPrice < banner.originalPrice && (
            <Typography
              sx={{
                fontFamily: "'Space Mono', monospace",
                fontSize: 13,
                color: 'rgba(251,246,236,0.55)',
                textDecoration: 'line-through',
                pb: '6px',
              }}
            >
              {fmtINR(banner.originalPrice)}
            </Typography>
          )}
        </Box>

        {/* CTA */}
        <Box sx={{ position: 'relative', zIndex: 2, mt: { xs: 2, md: 2.5 } }}>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              if (banner.href) navigate(banner.href);
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              padding: '12px 22px',
              borderRadius: '10px',
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 600,
              fontSize: 14,
              textTransform: 'none',
              bgcolor: '#F0B429',
              color: '#1A3A2A',
              boxShadow: '0 8px 24px -8px rgba(240,180,41,0.5)',
              '&:hover': { bgcolor: '#FFC23A' },
            }}
          >
            {banner.cta || 'Book now'} →
          </Button>
        </Box>
      </Box>
    </Box>
  );
};


const HomeBanners = () => {
  const navigate = useNavigate();
  const { data: offers = [] } = useListOffersQuery();
  const slides = useMemo(() => buildSlides(offers), [offers]);

  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => { setActive(0); }, [slides.length]);

  useEffect(() => {
    if (paused || slides.length <= 1) return undefined;
    const id = setInterval(() => setActive((i) => (i + 1) % slides.length), ROTATE_MS);
    return () => clearInterval(id);
  }, [paused, slides.length]);

  if (slides.length === 0) return null;

  const banner = slides[active] || slides[0];
  const hasImage = !!banner.bannerUrl;

  return (
    <Box sx={{ bgcolor: 'var(--cream)' }}>
      <Box
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onClick={() => banner.href && navigate(banner.href)}
        role={banner.href ? 'link' : undefined}
        tabIndex={banner.href ? 0 : undefined}
        onKeyDown={(e) => {
          if (banner.href && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            navigate(banner.href);
          }
        }}
        sx={{
          position: 'relative',
          width: '100%',
          overflow: 'hidden',
          // Image mode keeps the dark forest fill; text mode sits on a
          // soft warm background that the boutique card lifts off of.
          background: hasImage ? 'var(--forest)' : 'var(--cream)',
          color: '#fff',
          // Fixed height on desktop (per design intent: "same height
          // regardless of content"); auto on mobile so the stacked
          // card doesn't clip — vertical stacking only happens at xs/sm
          // and naturally needs more height than 65vh of a phone screen.
          height: { xs: 'auto', md: '65vh' },
          minHeight: { xs: 480, md: '65vh' },
          transition: 'background 0.6s ease',
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'center',
          // Sale-burst banner needs the full width to look right; older
          // boutique card centres itself via its own maxWidth. So the
          // outer wrapper drops its horizontal padding entirely.
          px: 0,
          py: 0,
          cursor: banner.href ? 'pointer' : 'default',
        }}
      >
        {/* Mode A — admin uploaded a banner image: render it full-bleed,
            with a soft gradient overlay so the dot indicators and any
            optional CTA layered on top stay readable. */}
        {hasImage && (
          <>
            <Box
              key={banner.id}
              component="img"
              src={banner.bannerUrl}
              alt={banner.title || 'Promotional banner'}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(180deg, rgba(0,0,0,0.0) 60%, rgba(0,0,0,0.55) 100%)',
                pointerEvents: 'none',
              }}
            />
            <Button
              variant="contained"
              onClick={(e) => {
                e.stopPropagation();
                navigate(banner.href);
              }}
              sx={{
                position: 'absolute',
                bottom: { xs: 60, md: 70 },
                left: { xs: 24, md: 64 },
                zIndex: 2,
                bgcolor: '#F0B429',
                color: '#1A3A2A',
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 700,
                px: 2.5,
                py: 1,
                boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
                '&:hover': { bgcolor: '#fff' },
              }}
            >
              {banner.cta}
            </Button>
          </>
        )}

        {/* Mode B — text-mode default banner.
            Every slide without an admin-uploaded image now renders
            with the EditorialTriBanner template (the previous slide-
            specific SaleBurstBanner / BoutiqueCard variants were
            retired per product call — we keep one consistent default
            look across the whole carousel). */}
        {!hasImage && <EditorialTriBanner banner={banner} navigate={navigate} />}

        {/* Dot indicators — overlaid on the banner so they don't push the
            section taller. Centered against the full banner width. */}
        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: { xs: 14, md: 22 },
            zIndex: 5,
            pointerEvents: 'none',
          }}
        >
          {slides.map((b, i) => (
            <Box
              key={b.id}
              component="button"
              type="button"
              onClick={(e) => { e.stopPropagation(); setActive(i); }}
              aria-label={`Show banner ${i + 1}`}
              sx={{
                width: i === active ? 28 : 8,
                height: 8,
                bgcolor: hasImage
                  ? (i === active ? '#fff' : 'rgba(255,255,255,0.4)')
                  : (i === active ? '#1A3A2A' : 'rgba(26,58,42,0.3)'),
                border: 'none',
                borderRadius: 100,
                cursor: 'pointer',
                transition: 'all 0.25s',
                p: 0,
                pointerEvents: 'auto',
              }}
            />
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default HomeBanners;
